// Markdown-file-based blog loader.
//
// CRA (webpack 5, via react-scripts) has no built-in "import raw text"
// loader like Vite's `import.meta.glob` — by default any non-JS/CSS/JSON
// asset (including .md) falls through to the "file" loader, which just
// returns a URL to the emitted file rather than its text content. Rather
// than eject or add a raw-loader (which needs a webpack config change we
// can't make without ejecting), we use `require.context` to discover every
// .md file in src/blog/ at build time and get its (webpack-emitted) URL,
// then `fetch()` that URL at runtime to read the actual markdown text.
// This works identically in `npm start` (dev server) and `npm run build`
// (static file copied into /static/media and fetched same-origin).
const blogContext = require.context('../blog', false, /\.md$/);

function parseFrontmatter(raw) {
  const match = /^---\s*\n([\s\S]*?)\n---\s*\n?([\s\S]*)$/.exec(raw);
  if (!match) {
    return { meta: {}, body: raw };
  }
  const [, frontmatter, body] = match;
  const meta = {};
  frontmatter.split('\n').forEach((line) => {
    const lineMatch = /^([A-Za-z0-9_]+):\s*(.*)$/.exec(line.trim());
    if (!lineMatch) return;
    const [, key, rawValue] = lineMatch;
    let value = rawValue.trim();
    if (
      (value.startsWith('"') && value.endsWith('"')) ||
      (value.startsWith("'") && value.endsWith("'"))
    ) {
      value = value.slice(1, -1);
    }
    meta[key] = value;
  });
  return { meta, body: body.trim() };
}

// Small hand-rolled markdown -> HTML renderer. Supports just what our posts
// need: headings (## / ###), paragraphs, unordered lists (- item), bold
// (**text**), italic (*text*) and links ([text](url)). Deliberately not a
// full CommonMark implementation — this is a lightweight blog, not a docs
// site, and pulling in a full markdown dependency for one renderer felt
// like overkill.
function renderInline(text) {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>')
    .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
    .replace(/\*([^*]+)\*/g, '<em>$1</em>');
}

function markdownToHtml(markdown) {
  const lines = markdown.split('\n');
  const html = [];
  let listBuffer = [];

  const flushList = () => {
    if (listBuffer.length) {
      html.push(`<ul>${listBuffer.map((item) => `<li>${renderInline(item)}</li>`).join('')}</ul>`);
      listBuffer = [];
    }
  };

  lines.forEach((rawLine) => {
    const line = rawLine.trim();
    if (!line) {
      flushList();
      return;
    }
    if (line.startsWith('### ')) {
      flushList();
      html.push(`<h3>${renderInline(line.slice(4))}</h3>`);
    } else if (line.startsWith('## ')) {
      flushList();
      html.push(`<h2>${renderInline(line.slice(3))}</h2>`);
    } else if (line.startsWith('# ')) {
      flushList();
      html.push(`<h2>${renderInline(line.slice(2))}</h2>`);
    } else if (line.startsWith('- ') || line.startsWith('* ')) {
      listBuffer.push(line.slice(2));
    } else {
      flushList();
      html.push(`<p>${renderInline(line)}</p>`);
    }
  });
  flushList();

  return html.join('\n');
}

// publish-approved-blog writes post bodies as already-rendered HTML
// (content_html), not markdown — its own frontmatter comment says so. Every
// pipeline post's body starts directly with a block tag (`<p>...`). Hand-
// written posts in this repo (e.g. hormones-across-life-stages.md) are real
// markdown, starting with a heading or plain text. Bug fix (18 Aug 2026):
// this used to run EVERY body through markdownToHtml() regardless, which
// treats each already-HTML line as literal markdown text and escapes its
// < and > via renderInline — so every pipeline post rendered its raw tags
// as visible text (`<p>...</p>` printed on the page) instead of being
// interpreted as HTML. Detecting already-HTML content and passing it
// through untouched (matching Riverside's blog.js, which never markdown-
// processes content_html at all) fixes pipeline posts while still
// rendering genuine hand-written markdown posts correctly.
function isAlreadyHtml(body) {
  return /^\s*</.test(body);
}

async function fetchPost(key) {
  const url = blogContext(key);
  const res = await fetch(url);
  const raw = await res.text();
  const { meta, body } = parseFrontmatter(raw);
  return {
    title: meta.title || 'Untitled post',
    date: meta.date || '',
    excerpt: meta.excerpt || '',
    slug: meta.slug || key.replace(/^\.\//, '').replace(/\.md$/, ''),
    html: isAlreadyHtml(body) ? body : markdownToHtml(body),
    schemaArticle: decodeSchema(meta.schema_article),
    schemaFaq: decodeSchema(meta.schema_faq),
  };
}

// schema_article / schema_faq frontmatter fields are base64-encoded JSON-LD
// (Article + optional FAQPage), written by publish-approved-blog. Base64
// because the frontmatter parser above is a naive key:-per-line reader with
// no support for the quotes/colons/braces raw JSON would contain — see that
// function's own comment. Returns null so BlogPost.js can render nothing
// when a post predates this field (rejected then republished, or a very
// old post that was never touched again).
function decodeSchema(b64) {
  if (!b64) return null;
  try {
    return decodeURIComponent(escape(atob(b64)));
  } catch (e) {
    console.error('[blog] could not decode schema frontmatter field:', e.message);
    return null;
  }
}

let cachedPosts = null;

export async function getAllPosts() {
  if (cachedPosts) return cachedPosts;
  const posts = await Promise.all(blogContext.keys().map(fetchPost));
  posts.sort((a, b) => new Date(b.date) - new Date(a.date));
  cachedPosts = posts;
  return posts;
}

export async function getPostBySlug(slug) {
  const posts = await getAllPosts();
  return posts.find((p) => p.slug === slug) || null;
}

export function formatPostDate(dateString) {
  if (!dateString) return '';
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return dateString;
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
}
